/**
 * DEVICE DETECTION UTILITIES
 *
 * Comprehensive device and browser detection functions
 * for responsive behavior across the application.
 *
 * FEATURES:
 * - Device type detection (mobile, tablet, desktop)
 * - Browser detection (Safari, Chrome, Firefox, etc.)
 * - Touch capability detection
 * - Screen size breakpoints
 * - OS detection (iOS, Android, Windows, macOS)
 * - Specific device detection (iPhone, iPad, Android tablet)
 */

/**
 * Get the user agent string
 */
export const getUserAgent = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return '';
  }
  return navigator.userAgent;
};

/**
 * Get current window dimensions
 */
export const getWindowSize = () => {
  if (typeof window === 'undefined') {
    return { width: 0, height: 0 };
  }
  return {
    width: window.innerWidth,
    height: window.innerHeight
  };
};

/**
 * Check if device has touch capability
 */
export const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;

  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0
  );
};

/**
 * Detect if device is an iPhone
 */
export const isIPhone = () => {
  const userAgent = getUserAgent();
  return /iPhone/i.test(userAgent);
};

/**
 * Detect if device is an iPad
 * Note: iPadOS 13+ reports as Mac, so we check for touch capability
 */
export const isIPad = () => {
  const userAgent = getUserAgent();
  if (typeof navigator === 'undefined') return false;

  return (
    /iPad/i.test(userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

/**
 * Detect if device is any iOS device
 */
export const isIOS = () => {
  return isIPhone() || isIPad();
};

/**
 * Detect if device is Android mobile phone
 */
export const isAndroidMobile = () => {
  const userAgent = getUserAgent();
  const { width } = getWindowSize();
  return /Android/i.test(userAgent) && width < 768;
};

/**
 * Detect if device is Android tablet
 */
export const isAndroidTablet = () => {
  const userAgent = getUserAgent();
  const { width } = getWindowSize();
  return /Android/i.test(userAgent) && width >= 768;
};

/**
 * Detect if device is any Android device
 */
export const isAndroid = () => {
  const userAgent = getUserAgent();
  return /Android/i.test(userAgent);
};

/**
 * Detect if device is a mobile phone (any OS)
 */
export const isMobile = () => {
  const { width } = getWindowSize();
  return isIPhone() || isAndroidMobile() || (width < 768 && isTouchDevice());
};

/**
 * Detect if device is a tablet (any OS)
 */
export const isTablet = () => {
  const { width } = getWindowSize();
  return (
    isIPad() ||
    isAndroidTablet() ||
    (width >= 768 && width <= 1024 && isTouchDevice())
  );
};

/**
 * Detect if device is desktop
 */
export const isDesktop = () => {
  return !isMobile() && !isTablet();
};

/**
 * Get device type as string
 * @returns {'mobile' | 'tablet' | 'ipad' | 'desktop'}
 */
export const getDeviceType = () => {
  if (isIPhone() || isAndroidMobile()) {
    return 'mobile';
  }
  if (isIPad()) {
    return 'ipad';
  }
  if (isTablet()) {
    return 'tablet';
  }
  return 'desktop';
};

/**
 * BROWSER DETECTION
 */

/**
 * Detect Safari browser
 */
export const isSafari = () => {
  const userAgent = getUserAgent();
  return /Safari/i.test(userAgent) && !/Chrome/i.test(userAgent);
};

/**
 * Detect Chrome browser
 */
export const isChrome = () => {
  const userAgent = getUserAgent();
  return /Chrome/i.test(userAgent) && !/Edge/i.test(userAgent);
};

/**
 * Detect Firefox browser
 */
export const isFirefox = () => {
  const userAgent = getUserAgent();
  return /Firefox/i.test(userAgent);
};

/**
 * Detect Edge browser
 */
export const isEdge = () => {
  const userAgent = getUserAgent();
  return /Edge/i.test(userAgent) || /Edg/i.test(userAgent);
};

/**
 * Detect Brave browser
 */
export const isBrave = () => {
  const userAgent = getUserAgent();
  return /Brave/i.test(userAgent) || /brave/i.test(userAgent);
};

/**
 * Get browser type as string
 * @returns {'safari' | 'chrome' | 'firefox' | 'edge' | 'brave' | 'unknown'}
 */
export const getBrowserType = () => {
  if (isBrave()) return 'brave';
  if (isEdge()) return 'edge';
  if (isChrome()) return 'chrome';
  if (isSafari()) return 'safari';
  if (isFirefox()) return 'firefox';
  return 'unknown';
};

/**
 * OPERATING SYSTEM DETECTION
 */

/**
 * Detect Windows OS
 */
export const isWindows = () => {
  const userAgent = getUserAgent();
  return /Windows/i.test(userAgent);
};

/**
 * Detect macOS
 */
export const isMacOS = () => {
  if (typeof navigator === 'undefined') return false;
  const userAgent = getUserAgent();

  return (
    /Macintosh/i.test(userAgent) ||
    /Mac OS/i.test(userAgent) ||
    (navigator.platform === 'MacIntel' && !isIPad())
  );
};

/**
 * Detect Linux OS
 */
export const isLinux = () => {
  const userAgent = getUserAgent();
  return /Linux/i.test(userAgent) && !isAndroid();
};

/**
 * Get operating system type
 * @returns {'ios' | 'android' | 'windows' | 'macos' | 'linux' | 'unknown'}
 */
export const getOSType = () => {
  if (isIOS()) return 'ios';
  if (isAndroid()) return 'android';
  if (isWindows()) return 'windows';
  if (isMacOS()) return 'macos';
  if (isLinux()) return 'linux';
  return 'unknown';
};

/**
 * SCREEN SIZE UTILITIES
 */

/**
 * Check if screen matches a specific breakpoint
 * Breakpoints based on Tailwind CSS defaults
 */
export const matchesBreakpoint = (breakpoint) => {
  const { width } = getWindowSize();

  const breakpoints = {
    'xs': 0,      // Extra small devices
    'sm': 640,    // Small devices (phones)
    'md': 768,    // Medium devices (tablets)
    'lg': 1024,   // Large devices (laptops)
    'xl': 1280,   // Extra large devices (desktops)
    '2xl': 1536   // 2X large devices (large desktops)
  };

  return width >= (breakpoints[breakpoint] || 0);
};

/**
 * Get current breakpoint
 * @returns {'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'}
 */
export const getCurrentBreakpoint = () => {
  const { width } = getWindowSize();

  if (width >= 1536) return '2xl';
  if (width >= 1280) return 'xl';
  if (width >= 1024) return 'lg';
  if (width >= 768) return 'md';
  if (width >= 640) return 'sm';
  return 'xs';
};

/**
 * ORIENTATION DETECTION
 */

/**
 * Check if device is in portrait orientation
 */
export const isPortrait = () => {
  const { width, height } = getWindowSize();
  return height > width;
};

/**
 * Check if device is in landscape orientation
 */
export const isLandscape = () => {
  const { width, height } = getWindowSize();
  return width > height;
};

/**
 * Get current orientation
 * @returns {'portrait' | 'landscape'}
 */
export const getOrientation = () => {
  return isPortrait() ? 'portrait' : 'landscape';
};

/**
 * COMPREHENSIVE DEVICE INFO
 */

/**
 * Get all device information at once
 * @returns {Object} Complete device information
 */
export const getDeviceInfo = () => {
  return {
    // Device types
    isMobile: isMobile(),
    isTablet: isTablet(),
    isDesktop: isDesktop(),
    isIPad: isIPad(),
    isIPhone: isIPhone(),
    deviceType: getDeviceType(),

    // Touch capability
    isTouchDevice: isTouchDevice(),

    // Browser
    browser: getBrowserType(),
    isSafari: isSafari(),
    isChrome: isChrome(),
    isFirefox: isFirefox(),
    isEdge: isEdge(),
    isBrave: isBrave(),

    // Operating System
    os: getOSType(),
    isIOS: isIOS(),
    // TODO: Build out Android detection coverage beyond current heuristics.
    isAndroid: isAndroid(),
    isWindows: isWindows(),
    isMacOS: isMacOS(),
    isLinux: isLinux(),

    // Screen
    screenWidth: getWindowSize().width,
    screenHeight: getWindowSize().height,
    breakpoint: getCurrentBreakpoint(),
    orientation: getOrientation(),
    isPortrait: isPortrait(),
    isLandscape: isLandscape(),

    // User Agent
    userAgent: getUserAgent()
  };
};

/**
 * Log device information (useful for debugging)
 */
export const logDeviceInfo = () => {
  const info = getDeviceInfo();
  console.group('🔍 Device Information');
  console.table({
    'Device Type': info.deviceType,
    'Is Mobile': info.isMobile,
    'Is Tablet': info.isTablet,
    'Is Desktop': info.isDesktop,
    'Touch Device': info.isTouchDevice,
    'Browser': info.browser,
    'OS': info.os,
    'Screen': `${info.screenWidth}x${info.screenHeight}`,
    'Breakpoint': info.breakpoint,
    'Orientation': info.orientation
  });
  console.groupEnd();

  return info;
};

export default {
  // Device detection
  isMobile,
  isTablet,
  isDesktop,
  isIPhone,
  isIPad,
  isAndroid,
  isAndroidMobile,
  isAndroidTablet,
  isTouchDevice,
  getDeviceType,

  // Browser detection
  isSafari,
  isChrome,
  isFirefox,
  isEdge,
  isBrave,
  getBrowserType,

  // OS detection
  isIOS,
  isWindows,
  isMacOS,
  isLinux,
  getOSType,

  // Screen utilities
  getWindowSize,
  matchesBreakpoint,
  getCurrentBreakpoint,
  isPortrait,
  isLandscape,
  getOrientation,

  // Comprehensive
  getDeviceInfo,
  logDeviceInfo
};
